/**
 * File heavily inspired by the d3-time package, that only expose browser timezone and utc timezone ticker.
 * https://github.com/d3/d3-time/blob/main/src/ticks.js
 *
 * It use luxon to manage any timezone
 */

import { bisector, type Numeric, tickStep, timeInterval, utcMillisecond, utcSecond } from "d3";
import { DateTime } from "luxon";

const durationSecond = 1000;
const durationMinute = durationSecond * 60;
const durationHour = durationMinute * 60;
const durationDay = durationHour * 24;
const durationWeek = durationDay * 7;
const durationMonth = durationDay * 30;
const durationYear = durationDay * 365;

export function createTzIntervals(tz: string = "local") {
	const floorUnit = (date: Date, unit: "year" | "month" | "day" | "hour" | "minute") => {
		const dt = DateTime.fromJSDate(date).setZone(tz).startOf(unit);
		date.setTime(dt.toMillis());
	};

	const offsetUnit = (
		date: Date,
		unit: "years" | "months" | "weeks" | "days" | "hours" | "minutes",
		step: number
	) => {
		const dt = DateTime.fromJSDate(date)
			.setZone(tz)
			.plus({ [unit]: step });
		date.setTime(dt.toMillis());
	};

	const tzYear = timeInterval(
		(date) => floorUnit(date, "year"),
		(date, step) => offsetUnit(date, "years", step),
		(start, end) =>
			DateTime.fromJSDate(end).setZone(tz).diff(DateTime.fromJSDate(start).setZone(tz), "years")
				.years,
		(date) => DateTime.fromJSDate(date).setZone(tz).year
	);

	const tzMonth = timeInterval(
		(date) => floorUnit(date, "month"),
		(date, step) => offsetUnit(date, "months", step),
		(start, end) =>
			DateTime.fromJSDate(end).setZone(tz).diff(DateTime.fromJSDate(start).setZone(tz), "months")
				.months,
		(date) => DateTime.fromJSDate(date).setZone(tz).month - 1
	);

	const tzSunday = timeInterval(
		(date) => {
			let dt = DateTime.fromJSDate(date).setZone(tz).startOf("day");
			const dayOfWeek = dt.weekday % 7; // Sunday = 0
			dt = dt.minus({ days: dayOfWeek });
			date.setTime(dt.toMillis());
		},
		(date, step) => offsetUnit(date, "weeks", step),
		(start, end) => (end.getTime() - start.getTime()) / durationWeek
	);

	const tzDay = timeInterval(
		(date) => floorUnit(date, "day"),
		(date, step) => offsetUnit(date, "days", step),
		(start, end) =>
			DateTime.fromJSDate(end).setZone(tz).diff(DateTime.fromJSDate(start).setZone(tz), "days")
				.days,
		(date) => DateTime.fromJSDate(date).setZone(tz).day - 1
	);

	const tzHour = timeInterval(
		(date) => floorUnit(date, "hour"),
		(date, step) => offsetUnit(date, "hours", step),
		(start, end) => (end.getTime() - start.getTime()) / durationHour,
		(date) => DateTime.fromJSDate(date).setZone(tz).hour
	);

	const tzMinute = timeInterval(
		(date) => floorUnit(date, "minute"),
		(date, step) => offsetUnit(date, "minutes", step),
		(start, end) => (end.getTime() - start.getTime()) / durationMinute,
		(date) => DateTime.fromJSDate(date).setZone(tz).minute
	);

	return { tzYear, tzMonth, tzSunday, tzDay, tzHour, tzMinute };
}

export function createTzTicker(tz?: string) {
	const { tzYear, tzMonth, tzSunday, tzDay, tzHour, tzMinute } = createTzIntervals(tz);

	const tickIntervals = [
		[utcSecond, 1, durationSecond],
		[utcSecond, 5, 5 * durationSecond],
		[utcSecond, 15, 15 * durationSecond],
		[utcSecond, 30, 30 * durationSecond],
		[tzMinute, 1, durationMinute],
		[tzMinute, 5, 5 * durationMinute],
		[tzMinute, 15, 15 * durationMinute],
		[tzMinute, 30, 30 * durationMinute],
		[tzHour, 1, durationHour],
		[tzHour, 3, 3 * durationHour],
		[tzHour, 6, 6 * durationHour],
		[tzHour, 12, 12 * durationHour],
		[tzDay, 1, durationDay],
		[tzDay, 2, 2 * durationDay],
		[tzSunday, 1, durationWeek],
		[tzMonth, 1, durationMonth],
		[tzMonth, 3, 3 * durationMonth],
		[tzYear, 1, durationYear],
	] as const;

	function ticks(start: Date, stop: Date, count: number = 10) {
		const reverse = stop < start;
		if (reverse) [start, stop] = [stop, start];
		const interval = tickInterval(start, stop, count);
		const result = interval ? interval.range(start, new Date(+stop + 1)) : [];
		return reverse ? result.reverse() : result;
	}

	function tickInterval(start: Numeric, stop: Numeric, count: number) {
		const target = Math.abs(stop.valueOf() - start.valueOf()) / count;
		const i = bisector(([, , step]: any) => step).right(tickIntervals, target);

		if (i === tickIntervals.length) {
			return tzYear.every(
				tickStep(start.valueOf() / durationYear, stop.valueOf() / durationYear, count)
			);
		}
		if (i === 0) {
			return utcMillisecond.every(Math.max(tickStep(start.valueOf(), stop.valueOf(), count), 1));
		}
		const [t, step] =
			tickIntervals[target / tickIntervals[i - 1][2] < tickIntervals[i][2] / target ? i - 1 : i];
		return t.every(step);
	}

	return { ticks, tickInterval };
}
